import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "../../PageLayout/SidebarLayout";
import ConnectorLogo from "../connectors/ConnectorLogo";
import ConnectConnectorDrawer from "../components/ConnectConnectorDrawer";
import { fetchCatalog, getApiErrorMessage } from "../connectors/integrationsService";

const BADGE = {
  available: "bg-[#EAF3DE] text-[#528416]",
  connected: "bg-[#D8F9F0] text-[#0F766E]",
  "coming-soon": "bg-[#FFF4E5] text-[#EF9F27]",
};

const BADGE_LABEL = {
  available: "Available",
  connected: "Connected",
  "coming-soon": "Coming soon",
};

function ConnectorCard({ item, onConnect }) {
  const comingSoon = item.status === "coming-soon";
  const connected = item.status === "connected";

  return (
    <article
      className={`h-full bg-white border border-[#E6E7EB] rounded-[12px] p-[19px] flex flex-col gap-4 ${
        comingSoon ? "opacity-70" : ""
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <ConnectorLogo type={item.type} initials={item.initials} size="lg" />
        <div className="min-w-0">
          <h3 className="text-[14px] leading-4 font-semibold text-[#031F36] truncate">
            {item.name}
          </h3>
          <p className="mt-0.5 text-[12px] leading-[14px] text-[#898F99] truncate">
            {item.authLabel || "—"}
          </p>
        </div>
      </div>

      <p className="text-[12px] leading-[14px] text-[#898F99] flex-1">
        {item.description || "No description"}
      </p>

      <div className="mt-auto flex flex-col gap-4">
        <span
          className={`inline-flex self-start items-center rounded-[20px] px-[9px] py-1 text-[12px] leading-[14px] font-medium ${BADGE[item.status]}`}
        >
          {BADGE_LABEL[item.status] || item.status}
        </span>

        {!connected && (
          <button
            type="button"
            disabled={comingSoon}
            onClick={() => onConnect(item)}
            className={`w-full h-9 rounded-lg text-[12px] leading-[14px] font-medium ${
              comingSoon
                ? "bg-[#F5F7FA] text-[#898F99] border border-[#E6E7EB] cursor-not-allowed"
                : "bg-[#031F36] text-white hover:bg-[#0A2A44]"
            }`}
          >
            Connect
          </button>
        )}
      </div>
    </article>
  );
}

export default function AddConnector() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchCatalog();
        if (!cancelled) setCatalog(items);
      } catch (err) {
        if (!cancelled) setError(getApiErrorMessage(err, "Could not load catalog."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const available = catalog.filter((c) => c.section === "available" || c.status === "available");
  const comingSoon = catalog.filter((c) => c.section === "coming-soon" || c.status === "coming-soon");

  const goBack = () => navigate("/settings?tab=integrations");

  return (
    <SidebarLayout pageTitle="Add Connector" breadcrumb="Dashboard / Settings / Add Connector">
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto w-full min-w-0">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F172A] hover:text-slate-700 mb-6"
        >
          <span aria-hidden="true">←</span> Back
        </button>

        {loading ? (
          <p className="text-sm text-gray-500">Loading connectors…</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : catalog.length === 0 ? (
          <p className="text-sm text-gray-500 py-10 text-center">No connectors available</p>
        ) : (
          <>
            <section className="mb-8">
              <h2 className="text-[14px] leading-4 font-medium text-[#898F99] uppercase mb-3">
                Available
              </h2>
              {available.length === 0 ? (
                <p className="text-sm text-gray-500 py-6">No available connectors</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {available.map((item) => (
                    <ConnectorCard key={item.id} item={item} onConnect={setActive} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="text-[14px] leading-4 font-medium text-[#898F99] uppercase mb-3">
                Coming soon
              </h2>
              {comingSoon.length === 0 ? (
                <p className="text-sm text-gray-500 py-6">No coming soon connectors</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {comingSoon.map((item) => (
                    <ConnectorCard key={item.id} item={item} onConnect={setActive} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {active && (
        <ConnectConnectorDrawer
          connector={active}
          onClose={() => setActive(null)}
          onSaved={goBack}
        />
      )}
    </SidebarLayout>
  );
}
